import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Spin,
  message,
  Divider,
  Typography,
  Switch,
  Space,
} from 'antd';
import {
  listLlms,
  listPromptScreens,
  createPromptAction,
  listPromptsAction,
} from '../../../../../store/actions/promptsv2.action';

import KeyValuePairEditor from './KeyValuePairEditor';
import { getSRTypeList } from 'src/modules/SuperAdmin/store/actions/service_request';
import {
  getLlmDisplayText,
  getLlmDisplayComponent,
  shouldShowTopP,
} from 'src/utils/llmUtils';
import { listAgentsAction } from 'src/modules/SuperAdmin/store/actions/agents.action';
import OrganizationFilter from '../../OrganizationFilter';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const CreatePromptModal = ({
  visible,
  setVisible,
  filters,
  selectedOrganizationUUID,
  scope,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isKeyValueMode, setIsKeyValueMode] = useState(false);

  const watchedConditionId = Form.useWatch('condition_id', form);
  const watchedPrompt = Form.useWatch('prompt', form);
  const watchedLlmId = Form.useWatch('llm_id', form);

  const { llms, llmsLoading, promptScreens, promptScreensLoading } =
    useSelector((state) => state.promptsState);
  const {
    conditions,
    conditionsLoading,
    specialties,
    specialtiesLoading,
    serviceRequestTypes,
    serviceRequestTypesLoading,
    agentsList,
    agentsLoading,
  } = useSelector((state) => ({
    conditions: state?.conditionState?.conditionsDropdown,
    conditionsLoading: state?.conditionState?.conditionsLoading,
    specialties: state?.specialtyState?.specialtiesDropdown,
    specialtiesLoading: state?.specialtyState?.specialtiesLoading,
    serviceRequestTypes: state?.serviceRequestState?.srTypeList,
    serviceRequestTypesLoading: state?.serviceRequestState?.srTypeListLoading,
    agentsList: state?.agentsState?.agentsList,
    agentsLoading: state?.agentsState?.agentsLoading,
  }));

  useEffect(() => {
    if (visible) {
      dispatch(listLlms());
      dispatch(listPromptScreens(null, null, scope));
      if (scope === 'service_request') {
        dispatch(getSRTypeList(selectedOrganizationUUID));
      }
      if (scope === 'agent') {
        dispatch(listAgentsAction(selectedOrganizationUUID));
      }
    }
    if (!visible) {
      form.resetFields();
    }
  }, [dispatch, visible, form, selectedOrganizationUUID, scope]);

  const onClose = () => {
    form.resetFields();
    setIsSubmitting(false);
    setVisible(false);
  };

  const toggleEditorMode = (checked) => {
    setIsKeyValueMode(checked);
  };

  // Check if selected LLM has reasoning_effort
  const selectedLlm = llms?.llms?.find((llm) => llm.llm_id === watchedLlmId);
  const hasReasoningEffort = selectedLlm?.reasoning_effort;
  const showTopP = shouldShowTopP(selectedLlm);

  // Determine if OrganizationFilter should be disabled based on scope
  const isOrganizationFilterDisabled = () => {
    // Global scope (organization filter should be disabled for specialty scope)
    return scope === 'specialty' || scope === 'magicflow';
  };

  const handleFinish = async (values) => {
    setIsSubmitting(true);
    let payload = { ...values, scope };

    // Check if selected LLM has reasoning_effort and set temperature to 1
    const selectedLlm = llms?.llms?.find((llm) => llm.llm_id === values.llm_id);
    if (selectedLlm?.reasoning_effort) {
      payload.temp = 1;
    }

    // Unset top_p for models that don't support it (reasoning models, Anthropic, etc.)
    if (!shouldShowTopP(selectedLlm)) {
      payload.top_p = null;
    }

    if (isKeyValueMode && typeof payload.prompt === 'string') {
      try {
        JSON.parse(payload.prompt);
      } catch (e) {
        message.error('Invalid JSON in prompt field');
        setIsSubmitting(false);
        return;
      }
    } else if (!isKeyValueMode && typeof payload.prompt !== 'string') {
      payload.prompt = String(payload.prompt || '');
    }

    if (payload.tools) {
      try {
        payload.tools = JSON.parse(payload.tools);
        if (!Array.isArray(payload.tools)) {
          message.error('Tools must be a valid JSON array!');
          setIsSubmitting(false);
          return;
        }
      } catch (e) {
        message.error('Invalid JSON format for tools!');
        setIsSubmitting(false);
        return;
      }
    } else {
      payload.tools = null;
    }

    dispatch(
      createPromptAction(payload, () => {
        message.success('Prompt created successfully!');
        onClose();
        setIsSubmitting(false);
        dispatch(
          listPromptsAction({ ...filters, scope }, selectedOrganizationUUID)
        );
      })
    );
  };

  return (
    <Modal
      title={<Text>Create New Prompt</Text>}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      maskClosable={false}
    >
      <Spin
        spinning={
          llmsLoading ||
          promptScreensLoading ||
          conditionsLoading ||
          specialtiesLoading ||
          agentsLoading ||
          isSubmitting
        }
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          onFinish={handleFinish}
        >
          {/* Organization Filter Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              Organization Scope
            </h3>
            <Form.Item label="Organization">
              <OrganizationFilter disabled={isOrganizationFilterDisabled()} />
            </Form.Item>
          </div>

          {/* Prompt Details Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              Prompt Details
            </h3>
            <Form.Item name="description" label="Description">
              <TextArea rows={1} placeholder="Enter description" />
            </Form.Item>
            <Form.Item
              name="prompt_screens_id"
              label="Prompt Screen"
              rules={[
                { required: true, message: 'Please select a prompt screen!' },
              ]}
            >
              <Select
                placeholder="Select a screen"
                loading={promptScreensLoading}
                showSearch
                optionLabelProp="label"
                filterOption={(input, option) => {
                  const children = option.children;
                  const textContent =
                    children?.props?.children?.[0]?.props?.children || '';
                  const descContent =
                    children?.props?.children?.[1]?.props?.children || '';
                  return (
                    textContent.toLowerCase().includes(input.toLowerCase()) ||
                    descContent.toLowerCase().includes(input.toLowerCase())
                  );
                }}
              >
                {promptScreens?.prompt_screens?.map((screen) => (
                  <Select.Option
                    key={screen.prompt_screens_id}
                    value={screen.prompt_screens_id}
                    label={screen.description || screen.prompt_screen}
                  >
                    <div className="flex flex-col">
                      <div className="font-medium">
                        {screen.description || screen.prompt_screen}
                      </div>
                      {screen.description && (
                        <div className="text-xs text-gray-500">
                          {screen.prompt_screen}
                        </div>
                      )}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {scope === 'specialty' && (
              <Form.Item
                name="condition_id"
                label="Condition"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select a condition"
                  loading={conditionsLoading}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={conditions?.map((condition) => ({
                    label: condition.condition_name,
                    value: condition.condition_id,
                  }))}
                />
              </Form.Item>
            )}

            {scope === 'service_request' && (
              <Form.Item
                name="service_request_type_id"
                label="Service Request Type"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select a service request type"
                  loading={serviceRequestTypesLoading}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={serviceRequestTypes?.map((serviceRequestType) => ({
                    label: serviceRequestType.service_request_type,
                    value: serviceRequestType.service_request_type_id,
                  }))}
                />
              </Form.Item>
            )}

            {scope === 'agent' && (
              <Form.Item
                name="agent_id"
                label="Agent"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select an agent"
                  loading={agentsLoading}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={agentsList?.agents?.map((agent) => ({
                    label: agent.name,
                    value: agent.agent_id,
                  }))}
                />
              </Form.Item>
            )}

            <Form.Item
              name="prompt"
              label="Prompt Template"
              rules={[
                {
                  required: true,
                  message: 'Please enter the prompt template!',
                },
              ]}
            >
              <div className="flex justify-end pb-1">
                <Space>
                  <Text>Text</Text>
                  <Switch
                    checked={isKeyValueMode}
                    onChange={toggleEditorMode}
                  />
                  <Text>Sections</Text>
                </Space>
              </div>
              {isKeyValueMode ? (
                <KeyValuePairEditor
                  value={watchedPrompt}
                  onChange={(value) => form.setFieldsValue({ prompt: value })}
                />
              ) : (
                <TextArea
                  rows={12}
                  placeholder="Enter prompt template"
                  value={watchedPrompt}
                  onChange={(e) =>
                    form.setFieldsValue({ prompt: e.target.value })
                  }
                />
              )}
            </Form.Item>
            <Form.Item name="num_questions" label="Number of Questions">
              <InputNumber
                placeholder="Enter number of questions"
                className="w-full"
                min={1}
                step={1}
              />
            </Form.Item>
          </div>

          {/* LLM Settings Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              LLM Settings
            </h3>
            <Form.Item
              name="llm_id"
              label="LLM"
              rules={[{ required: true, message: 'Please select a LLM!' }]}
            >
              <Select
                placeholder="Select a LLM"
                loading={llmsLoading}
                showSearch
                filterOption={(input, option) =>
                  (option.label || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                optionLabelProp="label"
                onChange={(value) => {
                  form.setFieldsValue({ llm_id: value });

                  // Find the selected LLM and pre-populate fields
                  const selectedLlm = llms?.llms?.find(
                    (llm) => llm.llm_id === value
                  );
                  if (selectedLlm) {
                    const hasReasoningEffort = selectedLlm?.reasoning_effort;

                    // Pre-populate all models with temp=0 and max_tokens=8096
                    if (!hasReasoningEffort) {
                      form.setFieldsValue({ temp: 0 });
                    }
                    form.setFieldsValue({ max_token: 8096 });

                    // Clear top_p if the model doesn't support it
                    if (!shouldShowTopP(selectedLlm)) {
                      form.setFieldsValue({ top_p: undefined });
                    }
                  }
                }}
              >
                {llms?.llms?.map((llm) => {
                  const displayText = getLlmDisplayText(llm);

                  return (
                    <Select.Option
                      key={llm.llm_id}
                      value={llm.llm_id}
                      label={displayText}
                    >
                      <div className="flex flex-col">
                        <div className="font-medium">
                          {getLlmDisplayComponent(llm)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {llm.provider || llm.description || llm.model_type}
                        </div>
                      </div>
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            {!hasReasoningEffort && (
              <Form.Item
                name="temp"
                label="Temperature"
                rules={[
                  { required: true, message: 'Please enter a temperature!' },
                  {
                    type: 'number',
                    min: 0,
                    max: 2,
                    message: 'Temperature must be between 0 and 2',
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter temperature (e.g., 0.7)"
                  className="w-full"
                  min={0}
                  max={2}
                  step={0.1}
                />
              </Form.Item>
            )}
            <Form.Item
              name="max_token"
              label="Max Tokens"
              rules={[
                { required: true, message: 'Please enter max tokens!' },
                {
                  type: 'number',
                  min: 1,
                  message: 'Max tokens must be at least 1',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter max tokens (e.g., 1024)"
                className="w-full"
                min={1}
                step={1}
              />
            </Form.Item>
            {showTopP && (
              <Form.Item
                name="top_p"
                label="Top P"
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: 1,
                    message: 'Top P must be between 0 and 1',
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter top p (e.g., 0.9)"
                  className="w-full"
                  min={0}
                  max={1}
                  step={0.01}
                />
              </Form.Item>
            )}
            <Form.Item
              name="tools"
              label="Tools (JSON Array)"
              rules={[
                { required: false, message: 'Please enter the tools JSON!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.resolve();
                    }
                    try {
                      const parsed = JSON.parse(value);
                      if (Array.isArray(parsed)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Tools must be a valid JSON array!')
                      );
                    } catch (e) {
                      return Promise.reject(new Error('Invalid JSON format!'));
                    }
                  },
                }),
              ]}
            >
              <TextArea
                rows={4}
                placeholder='Enter tools as a JSON array (e.g., ["tool1", "tool2"])'
                className="w-full"
              />
            </Form.Item>
          </div>

          <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
            {' '}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Create
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreatePromptModal;
